/**
 * Data Table component.
 * Token-based table matching CoordioCore's data table pattern.
 * Native HTML table with Tailwind CSS styling.
 */

import type { ReactNode } from 'react';

/**
 * Data Table column definition
 */
export interface DataTableColumn<T = any> {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  format?: (value: any, row: T) => ReactNode;
  render?: (row: T) => ReactNode;
}

/**
 * Data Table component props
 */
export interface DataTableProps<T = any> {
  columns: DataTableColumn<T>[];
  rows: T[];
  loading?: boolean;
  emptyMessage?: string;
  emptyState?: ReactNode;
  stickyHeader?: boolean;
  maxHeight?: number | string;
  size?: 'small' | 'medium';
  striped?: boolean;
  hover?: boolean;
  onRowClick?: (row: T) => void;
}

const alignClass = (align?: 'left' | 'right' | 'center') => {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
};

/**
 * Data Table component with CoordioCore token-based styling.
 */
const DataTable = <T extends Record<string, any>>({
  columns,
  rows,
  loading = false,
  emptyMessage = 'No data available',
  emptyState,
  stickyHeader: _stickyHeader = true,
  maxHeight,
  size = 'medium',
  striped: _striped = false,
  hover = true,
  onRowClick,
}: DataTableProps<T>) => {
  const cellPy = size === 'small' ? 'py-1' : 'py-2';

  if (loading) {
    return (
      <div className="rounded-lg border border-border overflow-hidden">
        {/* Header skeleton */}
        <div className="px-3 py-2 border-b border-border">
          <div className="flex gap-4">
            {columns.map((col) => (
              <div
                key={col.id}
                className="h-5 rounded bg-muted animate-pulse"
                style={{ width: col.minWidth || 100 }}
              />
            ))}
          </div>
        </div>
        {/* Row skeletons */}
        {[...Array(5)].map((_, i) => (
          <div key={i} className="px-3 py-2 border-b border-border/50">
            <div className="flex gap-4">
              {columns.map((col) => (
                <div
                  key={col.id}
                  className="h-5 rounded bg-muted animate-pulse"
                  style={{ width: col.minWidth || 100 }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    if (emptyState) {
      return <>{emptyState}</>;
    }
    return (
      <div className="p-8 text-center rounded-lg border border-border">
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-border overflow-auto shadow-none"
      style={{ maxHeight }}
    >
      <table className="min-w-[650px] w-full border-collapse">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                className={`${alignClass(column.align)} ${cellPy} px-4 text-sm font-semibold text-foreground bg-muted/50 border-b border-border sticky top-0`}
                style={{ minWidth: column.minWidth }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={`
                transition-colors duration-150
                ${onRowClick ? 'cursor-pointer' : 'cursor-default'}
                ${hover ? 'hover:bg-accent/50' : ''}
                ${rowIndex < rows.length - 1 ? 'border-b border-border' : ''}
              `}
            >
              {columns.map((column) => {
                const value = row[column.id];
                return (
                  <td
                    key={column.id}
                    className={`${alignClass(column.align)} ${cellPy} px-4 text-sm`}
                  >
                    {column.render ? column.render(row) : column.format ? column.format(value, row) : value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
